import { fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class ThemeResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Fetch a list of themes
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-theme-list/)
   * @param {Object} payload
   * @param {string} payload.workspaceId
   * @returns {Promise.<[ProjectDTO]>}
   */

  authorThemeList = async () => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_LIST,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_LIST_OK,
      constants.RTM_AUTHOR_THEME_LIST_ERROR,
    );
    return response.themePayload;
  };

  /**
   * Fetch a theme
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-theme-list/)
   * @param {Object} payload
   * @param {string} payload.workspaceId
   * @returns {Promise.<[ProjectDTO]>}
   */

   authorThemeGet = async ({
     themeId,
   }) => {
     const message = {
       type: constants.RTM_WORKSPACE_THEME_GET,
       themeId,
     };

     const response = await fetchFromRTM(
       this.connection,
       message,
       constants.RTM_WORKSPACE_THEME_GET_OK,
       constants.RTM_WORKSPACE_THEME_GET_ERROR,
     );
     return response.themePayload;
   };

  authorThemeCreate = async ({
    workspaceId,
    name,
    config,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_CREATE,
      workspaceId,
      name,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_CREATE_OK,
      constants.RTM_AUTHOR_THEME_CREATE_ERROR,
    );
    return response.theme;
  };

  authorThemeUpdate = async ({
    themeId,
    name,
    config,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_UPDATE,
      themeId,
      name,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_UPDATE_OK,
      constants.RTM_AUTHOR_THEME_UPDATE_ERROR,
    );
    return response.theme;
  };

  authorThemeDelete = async ({
    themeId,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_DELETE,
      themeId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_DELETE_OK,
      constants.RTM_AUTHOR_THEME_DELETE_ERROR,
    );
    return response.theme;
  };

  authorThemeVariantGet = async ({
    themeId,
    variantId,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_VARIANT_GET,
      themeId,
      variantId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_VARIANT_GET_OK,
      constants.RTM_AUTHOR_THEME_VARIANT_GET_ERROR,
    );
    return response.themeVariant;
  };

  authorThemeVariantCreate = async ({
    themeId,
    variantName,
    config,
    state,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_VARIANT_CREATE,
      themeId,
      variantName,
      config,
      state,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_VARIANT_CREATE_OK,
      constants.RTM_AUTHOR_THEME_VARIANT_CREATE_ERROR,
    );
    return response.themeVariant;
  };

  authorThemeVariantUpdate = async ({
    themeId,
    variantId,
    variantName,
    config,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_VARIANT_UPDATE,
      themeId,
      variantId,
      variantName,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_VARIANT_UPDATE_OK,
      constants.RTM_AUTHOR_THEME_VARIANT_UPDATE_ERROR,
    );
    return response.themeVariant;
  };

  authorThemeVariantDelete = async ({
    themeId,
    variantId,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_THEME_VARIANT_DELETE,
      themeId,
      variantId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_THEME_VARIANT_DELETE_OK,
      constants.RTM_AUTHOR_THEME_VARIANT_DELETE_ERROR,
    );
    return response.themeVariant;
  };

  /**
   * Fetches a summary list of collaborators belonging to given theme. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-theme-collaborator-summary/)
   * @param {Object} payload
   * @param {string} payload.themeId
   * @returns {Promise.<CollaboratorDto>}
   */
     authorThemeCollaboratorSummary = async ({ themeId }) => {
       const message = {
         type: constants.RTM_AUTHOR_THEME_COLLABORATOR_SUMMARY,
         themeId,
       };
  
       const response = await fetchFromRTM(
         this.connection,
         message,
         constants.RTM_AUTHOR_THEME_COLLABORATOR_SUMMARY_OK,
         constants.RTM_AUTHOR_THEME_COLLABORATOR_SUMMARY_ERROR
       );
       return response.collaborators;
     };

     /**
   * Allows the user to grant permission to an account over a theme. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/theme-permission-grant/)
   * @param {Object} payload
   * @param {array} payload.accountOrTeamIds
   * @param {string} payload.themeId
   * @param {string} payload.permissionLevel
   * @returns {Promise.<themeId: string, permissionLevel: string>}
   */
   themePermissionGrant = async ({
     accountOrTeamIds,
     themeId,
     collaboratorType,
     permissionLevel,
   }) => {
     const message = {
       type: constants.RTM_THEME_PERMISSION_GRANT,
       [`${collaboratorType}Ids`]: [accountOrTeamIds],
       themeId,
       permissionLevel,
     };

     const response = await fetchFromRTM(
       this.connection,
       message,
       constants.RTM_THEME_PERMISSION_GRANT_OK,
       constants.RTM_THEME_PERMISSION_GRANT_ERROR
     );
     return response;
   };

  /**
   * Allows the user to revoke theme permission of an account. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/theme-permission-revoke/)
   * @param {Object} payload
   * @param {string} payload.accountOrTeamId
   * @param {string} payload.collaboratorType
   * @param {string} payload.themeId
   * @returns {Promise.<void>}
   */
  themePermissionRevoke = async ({
    accountOrTeamId,
    collaboratorType,
    themeId,
  }) => {
    const message = {
      type: constants.RTM_THEME_PERMISSION_REVOKE,
      [`${collaboratorType}Id`]: accountOrTeamId,
      themeId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_THEME_PERMISSION_REVOKE_OK,
      constants.RTM_THEME_PERMISSION_REVOKE_ERROR
    );
    return response;
  };
}

export default ThemeResolver;
